import { qs, qsa, listen, contains, add, remove } from "@fluorescent/dom";

import { section } from "@/glow";
import { on, emit } from "@/glow/events";
import { prefersReducedMotion } from "@/lib/a11y";
import {
  intersectionWatcher,
  delayOffset,
  shouldAnimate,
} from "@/lib/animation";

const classes = {
  activeDot: "slideshow-navigation__dot--active",
  navigationLoader: "slideshow-navigation__dot-loader",
};

const selectors = {
  slide: "[data-slide]",
  swiper: ".swiper",
  navigationPrev: ".slideshow-navigation__navigation-button--previous",
  navigationNext: ".slideshow-navigation__navigation-button--next",
  navigationDots: ".slideshow-navigation__dots",
  navigationDot: ".slideshow-navigation__dot",
  navigationLoader: ".slideshow-navigation__dot-loader",
  animatableItems: ".animation--section-blocks > *",
  pageFooter: "footer",
  video: "[data-background-video]",
  videoSlide: "[data-slide][data-slide-type='video']",
};

section("slideshow", {
  onLoad() {
    this.events = [];
    this.enableAutoplay = this.container.dataset.enableAutoplay;
    this.autoplayDuration = this.container.dataset.autoplay;
    this.pageFooter = qs(selectors.pageFooter, document);
    this.slideshow = null;
    this.slideshowContainer = qs(selectors.swiper, this.container);
    this.slides = qsa(selectors.slide, this.container);
    this.events.push(
      listen(this.container, "focusin", () => this.handleFocus())
    );

    if (shouldAnimate(this.container)) {
      this.slideAnimations = this.slides.map(slide =>
        delayOffset(slide, [selectors.animatableItems], 3)
      );
      this.observer = intersectionWatcher(this.container);
    }

    if (this.slides.length > 1) {
      import(flu.chunks.swiper).then(
        ({
          Swiper,
          Navigation,
          Autoplay,
          Pagination,
          EffectFade,
          EffectCreative,
        }) => {
          const swiperOptions = {
            modules: [Navigation, Pagination],
            autoHeight: true,
            slidesPerView: 1,
            grabCursor: true,
            effect: "fade",
            fadeEffect: {
              crossFade: false,
            },
            watchSlidesProgress: true,
            loop: true,
            navigation: {
              nextEl: selectors.navigationNext,
              prevEl: selectors.navigationPrev,
            },
            preloadImages: false, // if this is true, it negates benefits of lazyloading
            pagination: {
              el: selectors.navigationDots,
              clickable: true,
              bulletActiveClass: classes.activeDot,
              bulletClass: "slideshow-navigation__dot",
              renderBullet: (_, className) => `
                <button class="${className}" type="button">
                  <div class="slideshow-navigation__dot-loader"></div>
                </button>`,
            },
            on: {
              afterInit: swiper => {
                this.handleBulletLabels();
                this.manageVideoState(swiper.realIndex);
              },
              slideChange: swiper => {
                this.manageVideoState(swiper.realIndex);
              },
              slideChangeTransitionEnd: () => {
                const slideEls = this.slides;
                setTimeout(() => {
                  slideEls.forEach(slide => {
                    const isActive = slide.classList.contains(
                      "swiper-slide-active"
                    );

                    slide.toggleAttribute("inert", !isActive);
                  });
                }, 50);
              },
            },
          };

          if (this.enableAutoplay === "true") {
            swiperOptions.modules.push(Autoplay);
            swiperOptions.autoplay = {
              delay: this.autoplayDuration,
              disableOnInteraction: false,
            };

            this.autoplayObserver = new IntersectionObserver(
              ([{ isIntersecting: visible }]) => {
                if (visible) {
                  this.playSlideshow();
                } else {
                  this.pauseSlideshow();
                }
              }
            );

            this.footerObserver = new IntersectionObserver(
              ([{ isIntersecting: visible }]) => {
                if (visible) {
                  this.autoplayObserver.disconnect();
                  this.pauseSlideshow();
                } else {
                  this.autoplayObserver.observe(this.slideshowContainer);
                }
              },
              { threshold: 0.8 }
            );

            // blocking this function within 2 levels of iframes allows for the section preview to show and animate in the theme editor
            if (window.frames < 2) {
              this.footerObserver.observe(this.pageFooter);
            }
          }

          // TODO: invert logic or revise rulset
          // eslint-disable-next-line no-negated-condition
          if (!prefersReducedMotion()) {
            swiperOptions.modules.push(EffectFade);
            swiperOptions.effect = "fade";
            swiperOptions.fadeEffect = {
              crossFade: false,
            };
          } else {
            swiperOptions.modules.push(EffectCreative);
            swiperOptions.effect = "creative";
            swiperOptions.creativeEffect = {
              prev: {
                opacity: 0.99,
              },
              next: {
                opacity: 1,
              },
            };
          }

          this.slideshow = new Swiper(this.slideshowContainer, swiperOptions);
          this.navigationLoaderEls = qsa(
            selectors.navigationLoader,
            this.container
          );
          emit("slideshow:initialized");
        }
      );
    }
  },

  resetLoaderAnimation(loader) {
    remove(loader, classes.navigationLoader);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // double RAF lets the document recompute styles
        add(loader, classes.navigationLoader);
      });
    });
  },

  pauseSlideshow() {
    this.slideshow.autoplay.stop();
    this.navigationLoaderEls.forEach(loader => {
      loader.style.animationPlayState = "paused";
    });
  },

  playSlideshow() {
    this.slideshow?.autoplay.start();
    this.navigationLoaderEls.forEach(loader => {
      this.resetLoaderAnimation(loader);
      loader.style.animationPlayState = "running";
    });
  },

  manageVideoState(realIndex) {
    const formatedIndex = realIndex + 1;
    const realIndexString = formatedIndex.toString();
    // Slides get duplicated when swiper loops so we need to find all the videos
    const allVideoSlides = qsa(selectors.videoSlide, this.container);

    allVideoSlides.forEach(slide => {
      const video = slide.querySelector(selectors.video);
      if (!video) return;

      const pauseBtn = slide.querySelector("[js-video-pause-button]");
      const slideIndex = slide.dataset.slide;

      if (realIndexString === slideIndex) {
        // don't auto-play if user paused via a11y button
        if (video.dataset.a11yPriorityPause === "true") {
          return;
        }

        this.setPlayPause(video, pauseBtn, "play");
      } else {
        this.setPlayPause(video, pauseBtn, "pause");
      }
    });
  },

  setPlayPause(video, pauseBtn, state) {
    if (!video) return;

    const {
      strings: { accessibility: strings },
      icons,
    } = window.theme;

    if (state === "pause") {
      video.pause();

      if (pauseBtn) {
        if (pauseBtn.dataset.pauseButtonContent === "icon") {
          pauseBtn.innerHTML = icons.play;
        } else {
          pauseBtn.innerText = strings.play_video;
        }
      }
    } else {
      video.play();

      if (pauseBtn) {
        if (pauseBtn.dataset.pauseButtonContent === "icon") {
          pauseBtn.innerHTML = icons.pause;
        } else {
          pauseBtn.innerText = strings.pause_video;
        }
      }
    }
  },

  handleFocus() {
    if (contains(document.body, "user-is-tabbing")) {
      this.pauseSlideshow();
    }
  },

  handleBulletLabels() {
    const bullets = qsa(selectors.navigationDot, this.container);

    bullets.forEach((bullet, index) => {
      const associatedSlide = this.slides[index];
      const { bulletLabel } = associatedSlide.dataset;
      bullet.setAttribute("aria-label", bulletLabel);
    });
  },

  handleBlockSelect(slideIndex) {
    const index = parseInt(slideIndex, 10);
    if (!this.slideshow) return;

    this.slideshow.slideTo(index);
    this.pauseSlideshow();

    // sync background video when slide/block is updated
    // swiper realIndex is 0-based, data-slide is 1-based
    setTimeout(() => {
      this.manageVideoState(index - 1);
    }, 50);
  },

  handleBlockDeselect() {
    this.playSlideshow();
  },

  onBlockSelect({ target }) {
    const { slide } = target.dataset;
    if (!slide) return;

    if (this.slideshow) {
      this.handleBlockSelect(slide);
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on("slideshow:initialized", () => {
          this.handleBlockSelect(slide);
        })
      );
    }
  },

  onBlockDeselect() {
    if (this.slideshow) {
      this.handleBlockDeselect();
    } else {
      // Listen for initalization if slideshow does not exist
      this.events.push(
        on("slideshow:initialized", () => {
          this.handleBlockDeselect();
        })
      );
    }
  },

  onUnload() {
    this.slideshow?.destroy();
    this.events.forEach(unsubscribe => unsubscribe());
    this.observer?.destroy();
    this.autoplayObserver?.disconnect();
    this.footerObserver?.disconnect();
  },
});
